import { Box, MenuItem, Stack, TextField } from "@mui/material";
import { Button, Typography } from "@platform/design-system";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import {
  CustomTextEditorFields,
  CustomTextModeToggle,
} from "./CustomTextEditorFields";
import { validateAmountInput, quotesUsdFormatter } from "./formatters";
import { useCustomTextEditor } from "./useCustomTextEditor";
import { usePopupEditorKeyboard } from "./usePopupEditorKeyboard";

import type { Column, GridApi, ICellEditorParams } from "ag-grid-community";
import type { JsonValue } from "../utils/popupCustomText";

export type FieldValueType = "enum" | "number" | "text";

/** Mutate __collapsedSiblings on a row node (outside component scope to avoid react-compiler). */
function writeCollapsedSibling(
  node: { data: Record<string, unknown> },
  columnField: string,
  key: string,
  value: unknown,
): void {
  const prev =
    (node.data.__collapsedSiblings as
      | Record<string, Record<string, unknown>>
      | undefined) ?? {};
  node.data.__collapsedSiblings = {
    ...prev,
    [key]: { ...prev[key], [columnField]: value },
  };
}

export interface FieldPopoverEditorOption {
  value: string;
  label: string;
}

export interface FieldPopoverEditorParams {
  /** Initial value to seed the form (raw row-data value). */
  initialFieldValue: unknown;
  /** Inferred value type — drives which input is rendered. */
  valueType: FieldValueType;
  /** Header label shown at the top of the popover. */
  fieldLabel: string;
  /** Subtitle shown under the header (e.g. "Number", "1 of 5 options"). */
  typeHint: string;
  /** Required when `valueType === "enum"`. */
  options?: FieldPopoverEditorOption[];
  /** Optional numeric constraints. */
  min?: number;
  max?: number;
  step?: number;
  integer?: boolean;
  /**
   * When provided, the popover also renders an Amount input. The value is
   * stored raw (number | null) in `__collapsedSiblings[amountKey][columnField]`
   * and the format override renders the formatted display.
   */
  amountKey?: string;
  /** Initial raw amount value from the collapsed sibling. */
  initialAmount?: number | null;
  /**
   * When a field has more than one type-dependent amount sibling (e.g.
   * dollarAmount vs salaryMultiplier), maps each type value to the sibling
   * key that actually represents it, so the editor writes to the one
   * matching whichever type is currently selected instead of always
   * `amountKey` (CD-2568). Absent for the common single-amount case.
   */
  amountKeysByType?: Record<string, string>;
  /** Each candidate amount sibling's own current value, keyed the same as `amountKeysByType`. */
  amountValuesByKey?: Record<string, number | null>;
  /** Label for the amount input (defaults to "Amount"). */
  amountLabel?: string;
  /** Optional analyst-authored text attached to this popup-edited field. */
  initialCustomText?: string | null;
  /** Called on save when this popup supports custom text. */
  onCustomTextChange?: (
    customText: string | null,
    originalValue?: JsonValue,
  ) => void;
}

type Props = ICellEditorParams & FieldPopoverEditorParams;

const SENTINEL_VALUES = new Set([
  "notIncluded",
  "notDisclosed",
  "notOffered",
  "notApplicable",
]);

/**
 * Generic AG Grid popup cell editor for a single scalar benefit field.
 *
 * Renders a MUI input matched to the field's value-type
 * (enum → Select, number → numeric TextField, text → TextField) and
 * saves the the value via AG Grid's standard `valueSetter` (no manual
 * `node.setDataValue` needed — the cell's own column is what's edited).
 *
 * Wired via the Quotes-tab `CellEditorOverride` for any non-typed-amount
 * cell whose cellSpec uses an `ag*` editor (or no editor) so we don't
 * shadow specialized custom editors like `PopoverListEditor`.
 */
const toAmountString = (v: number | null | undefined): string =>
  v === undefined || v === null ? "" : String(v);

const writeSiblingNode = (
  api: GridApi,
  column: Column,
  benefitKey: string,
  value: unknown,
): void => {
  api.forEachNode((n) => {
    if (n.data?.benefitKey === benefitKey) {
      n.setDataValue(column, value);
    }
  });
};

/**
 * Persist the Amount input's value for a benefit that supports it.
 * Mirrors the render gate: with no concrete type selected (empty or a
 * sentinel) the amount input is hidden, so never persist a stale amount
 * that would otherwise linger in draftAmount from an earlier selection.
 *
 * Also clears the amount when the type is reclassified between two
 * different concrete (non-sentinel) values — e.g. Dollar Amount ↔
 * Percentage of Salary — and the user didn't also touch the amount in
 * this same edit. A number entered under one type's meaning shouldn't
 * silently carry over as if entered under the other's (CD-2552).
 */
const persistAmountValue = (
  draft: string,
  draftAmount: string,
  amountKey: string,
  columnField: string | undefined,
  column: Column | undefined,
  node: { data?: Record<string, unknown> } | null | undefined,
  api: GridApi,
  initialType: string,
  initialAmount: number | null | undefined,
): void => {
  const noConcreteType = draft === "" || SENTINEL_VALUES.has(draft);
  const parsedAmount = draftAmount === "" ? null : Number(draftAmount);

  const typeReclassified =
    initialType !== "" &&
    !SENTINEL_VALUES.has(initialType) &&
    draft !== "" &&
    !SENTINEL_VALUES.has(draft) &&
    draft !== initialType;
  const amountUntouched = draftAmount === toAmountString(initialAmount);
  const staleAmount = typeReclassified && amountUntouched;

  const amountValue =
    noConcreteType || staleAmount || !Number.isFinite(parsedAmount)
      ? null
      : parsedAmount;

  // 1. Write to __collapsedSiblings for display (the amount row is
  //    typically filtered out, so the format override reads from here).
  if (columnField && node?.data) {
    writeCollapsedSibling(
      node as { data: Record<string, unknown> },
      columnField,
      amountKey,
      amountValue,
    );
  }

  // 2. Also write via setDataValue so that if the amount row IS in
  //    the grid, AG Grid's standard pipeline fires (valueSetter →
  //    onCellValueChanged → onCoverageChange).
  if (column) writeSiblingNode(api, column, amountKey, amountValue);
};

/**
 * Persist the Amount input's value for a field with several independent
 * type-dependent amount siblings (e.g. dollarAmount vs salaryMultiplier).
 * Each sibling already tracks its own value and was reseeded from it when
 * the type changed, so — unlike `persistAmountValue`'s single shared slot —
 * there's no stale carry-over to guard against; persist the draft as-is to
 * the sibling matching the currently selected type (CD-2540).
 */
const persistTypedAmountValue = (
  draft: string,
  draftAmount: string,
  activeAmountKey: string,
  columnField: string | undefined,
  column: Column | undefined,
  node: { data?: Record<string, unknown> } | null | undefined,
  api: GridApi,
): void => {
  const parsedAmount = draftAmount === "" ? null : Number(draftAmount);
  const amountValue =
    SENTINEL_VALUES.has(draft) || draft === "" || !Number.isFinite(parsedAmount)
      ? null
      : parsedAmount;

  if (columnField && node?.data) {
    writeCollapsedSibling(
      node as { data: Record<string, unknown> },
      columnField,
      activeAmountKey,
      amountValue,
    );
  }
  if (column) writeSiblingNode(api, column, activeAmountKey, amountValue);
};

export const FieldPopoverEditor = forwardRef<unknown, Props>(
  function FieldPopoverEditor(props, ref) {
    const {
      initialFieldValue,
      valueType,
      fieldLabel,
      typeHint,
      options = [],
      min,
      max,
      step,
      integer,
      amountKey,
      initialAmount,
      amountKeysByType,
      amountValuesByKey,
      amountLabel = "Amount",
      initialCustomText,
      onCustomTextChange,
      stopEditing,
      node,
      colDef,
      column,
      api,
    } = props;

    const [draft, setDraft] = useState<string>(
      toInputString(initialFieldValue),
    );
    const [draftAmount, setDraftAmount] = useState<string>(
      toAmountString(initialAmount),
    );
    const customText = useCustomTextEditor(
      initialCustomText,
      onCustomTextChange,
      stopEditing,
    );
    const { editingCustomText } = customText;
    // Held outside React state so AG Grid's `getValue()` (called after
    // `stopEditing()`) sees the latest draft regardless of render timing.
    const lastValueRef = useRef<unknown>(initialFieldValue);

    useEffect(() => {
      setDraft(toInputString(initialFieldValue));
      lastValueRef.current = initialFieldValue;
    }, [initialFieldValue]);

    useEffect(() => {
      setDraftAmount(toAmountString(initialAmount));
    }, [initialAmount]);

    // When a field has more than one type-dependent amount sibling (e.g.
    // dollarAmount vs salaryMultiplier), the one actually being edited
    // depends on which type is currently selected, not just whichever was
    // configured first (CD-2568). Reseed the amount input from that
    // sibling's own value whenever the selected type changes it.
    const activeAmountKey = amountKeysByType?.[draft] ?? amountKey;
    const previousActiveAmountKeyRef = useRef(activeAmountKey);
    useEffect(() => {
      if (!amountKeysByType) return;
      if (previousActiveAmountKeyRef.current === activeAmountKey) return;
      previousActiveAmountKeyRef.current = activeAmountKey;
      const nextValue = activeAmountKey
        ? (amountValuesByKey?.[activeAmountKey] ?? null)
        : null;
      setDraftAmount(toAmountString(nextValue));
    }, [activeAmountKey, amountKeysByType, amountValuesByKey]);

    const inputRef = useRef<HTMLInputElement | null>(null);
    useEffect(() => {
      inputRef.current?.focus();
      inputRef.current?.select?.();
    }, []);

    useImperativeHandle(ref, () => ({
      getValue: () => lastValueRef.current,
      isPopup: () => true,
      isCancelBeforeStart: () => false,
      isCancelAfterEnd: () => false,
    }));

    const valueFieldError = fieldError(
      computeValueError(draft, valueType, editingCustomText, {
        min,
        max,
        integer,
      }),
    );
    const amountFieldError = fieldError(
      computeAmountError(
        draftAmount,
        draft,
        activeAmountKey,
        editingCustomText,
      ),
    );
    const hasValidationError = valueFieldError.error || amountFieldError.error;

    const handleSave = (): void => {
      if (editingCustomText) {
        lastValueRef.current = initialFieldValue;
        customText.saveCustomText(initialFieldValue);
        return;
      }

      if (hasValidationError) return;

      lastValueRef.current = parseDraft(draft, valueType);
      // Only clear an existing custom text — don't fire a request when
      // there was never one set for this cell.
      if (initialCustomText) onCustomTextChange?.(null);

      const columnField = colDef?.field;

      if (api?.stopEditing) {
        api.stopEditing(true);
      } else {
        stopEditing(true);
      }

      setTimeout(() => {
        if (activeAmountKey && amountKeysByType) {
          persistTypedAmountValue(
            draft,
            draftAmount,
            activeAmountKey,
            columnField,
            column,
            node,
            api,
          );
        } else if (activeAmountKey) {
          persistAmountValue(
            draft,
            draftAmount,
            activeAmountKey,
            columnField,
            column,
            node,
            api,
            toInputString(initialFieldValue),
            initialAmount,
          );
        }

        // Write the primary value via setDataValue so onCellValueChanged fires
        // unconditionally — including when only a collapsed sibling (e.g.
        // duration) changed but the primary value stayed the same.
        if (column && node) {
          node.setDataValue(column, lastValueRef.current);
        }

        // Full-row refresh so every cell in the row re-runs the format
        // override with the updated __collapsedSiblings (the edited cell
        // itself is refreshed by AG Grid, but sibling columns need a nudge).
        if (node && api?.refreshCells) {
          api.refreshCells({ rowNodes: [node], force: true });
        }
      }, 0);
    };

    const cancel = (): void => {
      lastValueRef.current = initialFieldValue;
      stopEditing(true);
    };

    const containerRef = useRef<HTMLDivElement | null>(null);
    usePopupEditorKeyboard(containerRef, handleSave, cancel);

    return (
      <Box
        ref={containerRef}
        sx={{
          p: 2,
          width: 440,
          backgroundColor: "background.paper",
          boxShadow: 3,
          borderRadius: 1,
        }}
      >
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="subtitle2" color="textSecondary">
              {fieldLabel}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {typeHint}
            </Typography>
          </Box>

          {customText.customTextSupported && (
            <CustomTextModeToggle
              editMode={customText.editMode}
              onChange={customText.setEditMode}
            />
          )}

          {editingCustomText && (
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ display: "block", mt: -0.5 }}
            >
              {`Benefit value: ${formatBenefitValue(
                initialFieldValue,
                valueType,
                options,
                amountKey,
                initialAmount,
                amountLabel,
              )}`}
            </Typography>
          )}

          {!editingCustomText && valueType === "enum" && (
            <TextField
              select
              size="small"
              label="Value"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              fullWidth
              inputRef={inputRef}
            >
              {options.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          )}

          {!editingCustomText && valueType !== "enum" && (
            <TextField
              size="small"
              type={valueType === "number" ? "number" : "text"}
              label="Value"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              {...valueFieldError}
              inputProps={numberInputProps(valueType, min, max, step, integer)}
              fullWidth
              inputRef={inputRef}
            />
          )}

          {!editingCustomText &&
            activeAmountKey &&
            draft !== "" &&
            !SENTINEL_VALUES.has(draft) && (
              <TextField
                size="small"
                type="number"
                label={amountLabel}
                value={draftAmount}
                onChange={(e) => setDraftAmount(e.target.value)}
                {...amountFieldError}
                inputProps={{ min: 0 }}
                fullWidth
              />
            )}

          {editingCustomText && (
            <CustomTextEditorFields
              content={customText.draftCustomText}
              onUpdate={customText.setDraftCustomText}
              onCharacterCount={customText.setDraftCharacterCount}
            />
          )}

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="sm" variant="secondary" onClick={cancel}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={handleSave}
              disabled={hasValidationError}
            >
              Save
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  },
);

const formatBenefitValue = (
  value: unknown,
  valueType: FieldValueType,
  options: FieldPopoverEditorOption[],
  amountKey: string | undefined,
  initialAmount: number | null | undefined,
  _amountLabel: string,
): string => {
  let base: string;
  if (value === null || value === undefined || value === "") {
    base = "\u2014";
  } else if (valueType === "enum") {
    base = options.find((o) => o.value === value)?.label ?? String(value);
  } else if (valueType === "number") {
    const n = typeof value === "number" ? value : Number(value);
    base = Number.isFinite(n) ? n.toLocaleString() : "\u2014";
  } else {
    base = String(value) || "\u2014";
  }

  if (amountKey && initialAmount !== null && initialAmount !== undefined) {
    const n =
      typeof initialAmount === "number" ? initialAmount : Number(initialAmount);
    const formatted = Number.isFinite(n)
      ? quotesUsdFormatter.format(n)
      : "\u2014";
    return formatted;
  }

  return base;
};

const toInputString = (value: unknown): string => {
  if (value === undefined || value === null) return "";
  if (typeof value === "number")
    return Number.isFinite(value) ? String(value) : "";
  return String(value);
};

const fieldError = (
  message: string,
): { error: boolean; helperText: string | undefined } => ({
  error: message !== "",
  helperText: message || undefined,
});

const numberInputProps = (
  valueType: FieldValueType,
  min: number | undefined,
  max: number | undefined,
  step: number | undefined,
  integer: boolean | undefined,
): { min?: number; max?: number; step: number | string } | undefined =>
  valueType === "number"
    ? { min, max, step: integer ? 1 : (step ?? "any") }
    : undefined;

const computeValueError = (
  draft: string,
  valueType: FieldValueType,
  editingCustomText: boolean,
  { min, max, integer }: { min?: number; max?: number; integer?: boolean },
): string => {
  if (editingCustomText || valueType !== "number" || draft === "") return "";

  return validateAmountInput(draft, {
    min: min ?? 0,
    max,
    integer: integer ?? false,
  });
};

const computeAmountError = (
  draftAmount: string,
  draft: string,
  amountKey: string | undefined,
  editingCustomText: boolean,
): string => {
  if (
    editingCustomText ||
    !amountKey ||
    SENTINEL_VALUES.has(draft) ||
    draftAmount === ""
  ) {
    return "";
  }

  return validateAmountInput(draftAmount, { min: 0, integer: false });
};

const parseDraft = (draft: string, valueType: FieldValueType): unknown => {
  if (draft === "") return null;
  if (valueType === "number") {
    const n = Number(draft);
    return Number.isFinite(n) ? n : null;
  }
  return draft;
};
